import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MagnifyingGlass, FunnelSimple } from "@phosphor-icons/react";
import { supabase } from "../lib/supabase";
import ActivityCard from "../components/ActivityCard";

const categories = [
  { v: "", l: "Todas" },
  { v: "yoga", l: "Yoga" },
  { v: "pilates", l: "Pilates" },
  { v: "fisioterapia", l: "Fisioterapia" },
  { v: "gym", l: "Gym" },
  { v: "padel", l: "Pádel" },
  { v: "crossfit", l: "CrossFit" },
  { v: "spinning", l: "Spinning" },
];

const Activities = () => {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const category = params.get("category") || "";

  useEffect(() => {
    setLoading(true);
    (async () => {
      const today = new Date().toISOString().slice(0, 10);
      let query = supabase
        .from("offers")
        .select("*, business:businesses(*)")
        .eq("status", "active")
        .gte("date", today)
        .order("date", { ascending: true })
        .order("start_time", { ascending: true })
        .limit(120);
      if (q) query = query.ilike("title", `%${q}%`);
      const { data, error } = await query;
      let rows = data || [];
      if (category) rows = rows.filter((o) => o.business?.category === category);
      if (!error) setItems(rows);
      setLoading(false);
    })();
  }, [category, q]);

  const setCategory = (v) => {
    const next = new URLSearchParams(params);
    if (v) next.set("category", v); else next.delete("category");
    setParams(next);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-20" data-testid="activities-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <span className="text-[11px] uppercase tracking-[0.3em] text-sage-deep mb-3 block">/ catálogo</span>
        <h1 className="font-heading text-4xl md:text-5xl font-light leading-tight mb-3 text-forest">
          Explora todas las <span className="serif-italic font-medium text-sage-deep">ofertas</span>
        </h1>
        <p className="text-stone max-w-2xl">Filtra por categoría o palabra clave y reserva en segundos.</p>
      </motion.div>

      <div className="mt-10 grid lg:grid-cols-[260px_1fr] gap-8">
        <aside className="space-y-6 lg:sticky lg:top-24 self-start bg-white border border-forest/8 rounded-2xl p-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-stone"><MagnifyingGlass size={14} /> Buscar</div>
            <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ej. vinyasa…"
              className="w-full bg-mist border border-forest/10 px-4 py-3 text-sm focus:border-sage-deep focus:outline-none rounded-md" />
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-stone"><FunnelSimple size={14} /> Categoría</div>
            <div className="flex flex-wrap gap-2">
              {categories.map((t) => (
                <button key={t.v || "all"} data-testid={`filter-category-${t.v || "all"}`} onClick={() => setCategory(t.v)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider transition border rounded-full ${
                    category === t.v ? "bg-sage-deep text-paper border-sage-deep" : "border-forest/15 text-stone hover:border-sage-deep hover:text-sage-deep"
                  }`}>
                  {t.l}
                </button>
              ))}
            </div>
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => <div key={i} className="aspect-[4/3] bg-mist animate-pulse rounded-2xl" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-24 border border-dashed border-forest/15 rounded-2xl">
              <p className="text-stone">Sin resultados. Prueba otros filtros.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="offers-grid">
              {items.map((a, i) => <ActivityCard key={a.id} offer={a} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Activities;
